import { Controller } from '@hotwired/stimulus';

const SCROLL_KEY = 'pagination:scrollToSection';

export default class extends Controller {
  static targets = ['perPage', 'page'];

  // A carbon footprint tab renders several independent tables, each with its own pagination
  // control, so the parameter names cannot be hardcoded to page/per_page - the partial
  // passes the scoped names it rendered its inputs with. The defaults keep the
  // single-collection index pages working unchanged.
  //
  // `scrollToSection` is on for those scoped tables only: paging reloads the whole tab, which
  // otherwise leaves the user at the top of it, far from the table they were browsing. The
  // single-collection index pages have nothing above their table to scroll past.
  static values = {
    pageParam: { type: String, default: 'page' },
    perPageParam: { type: String, default: 'per_page' },
    scrollToSection: { type: Boolean, default: false },
  };

  connect() {
    // A cached preview connects this controller too, before the real page replaces it -
    // consuming the key there would leave nothing for the page the user actually lands on.
    if (document.documentElement.hasAttribute('data-turbo-preview')) return;
    if (!this.scrollToSectionValue || readScrollKey() !== this.pageParamValue) return;

    clearScrollKey();
    const pageParam = this.pageParamValue;
    // Turbo scrolls to the top once a visit has rendered, which is after this element
    // connects, so scrolling here would be undone. Turbo marks <html> aria-busy for the
    // length of a visit and fires turbo:load after its own scroll.
    if (document.documentElement.hasAttribute('aria-busy')) {
      document.addEventListener('turbo:load', () => scrollToSection(pageParam), { once: true });
    } else {
      scrollToSection(pageParam);
    }
  }

  paginate() {
    this.rememberSection();
    let url = new URL(window.location.href);
    url.searchParams.set(this.pageParamValue, this.pageTarget.value);
    url.searchParams.set(this.perPageParamValue, this.perPageTarget.value);
    Turbo.visit(url);
  }

  // Bound to clicks anywhere in the control; only the first/prev/next/last links navigate.
  rememberSection(event) {
    if (!this.scrollToSectionValue) return;
    if (event && !event.target.closest('a')) return;

    try {
      sessionStorage.setItem(SCROLL_KEY, this.pageParamValue);
    } catch (_e) {
      // Storage blocked: paging still works, it just doesn't scroll.
    }
  }
}

function readScrollKey() {
  try {
    return sessionStorage.getItem(SCROLL_KEY);
  } catch (_e) {
    return null;
  }
}

function clearScrollKey() {
  try {
    sessionStorage.removeItem(SCROLL_KEY);
  } catch (_e) {
    // Nothing to clear if storage is blocked.
  }
}

// Looked up in the live document rather than through the connecting element, which may be
// gone by the time turbo:load fires.
function scrollToSection(pageParam) {
  const control = document.querySelector(
    `[data-controller~="pagination"][data-pagination-page-param-value="${pageParam}"]`
  );
  const section = control?.closest('[data-pagination-section]');
  if (section) section.scrollIntoView({ block: 'start' });
}
